'use client';

import type { ReactNode } from 'react';
import { createContext, use } from 'react';
import type { Workspace as MonacoWorkspace } from 'modern-monaco';

const PREVIEW_FILE = 'preview.json';
const MONACO_MODULE_URL = 'https://esm.sh/modern-monaco@0.4.0';

type MonacoRangeLike = { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number };
type MonacoEditModel = import('modern-monaco/editor-core').editor.ITextModel & {
  applyEdits: (operations: { range: unknown; text: string }[]) => void;
  getFullModelRange: () => MonacoRangeLike;
  getValue: () => string;
  pushStackElement?: () => void;
};
type MonacoEditor = import('modern-monaco/editor-core').editor.IStandaloneCodeEditor;
type MonacoNamespace = typeof import('modern-monaco/editor-core') & { Range: new (startLineNumber: number, startColumn: number, endLineNumber: number, endColumn: number) => unknown };
type WorkspaceHistory = { state: { current?: string }; push: (href: string) => void };
type WorkspaceWithInternals = MonacoWorkspace & {
  _monaco?: { promise: Promise<MonacoNamespace> };
  _openTextDocument?: (monaco: MonacoNamespace, editor: MonacoEditor, uri: string | URL, selectionOrPosition?: unknown, readonlyContent?: string) => Promise<MonacoEditModel>;
  __a2uiPatched?: boolean;
  history: WorkspaceHistory;
};

export type A2UIMonacoRuntime = {
  loadModernMonaco: () => Promise<typeof import('modern-monaco')>;
  ensureWorkspace: (initialFilename: string, initialValue: string) => Promise<WorkspaceWithInternals>;
  modelViewStateCache: Map<string, ReturnType<MonacoEditor['saveViewState']>>;
};

const A2UIMonacoRuntimeContext = createContext<A2UIMonacoRuntime | null>(null);
let singletonRuntime: A2UIMonacoRuntime | null = null; // Keep one module-level runtime for the whole route group; if the provider recreated this per navigation, Monaco/workspace identity would silently reset.

const toFileUri = (filename: string) => `file:///${filename.replace(/^\/+/, '')}`;
const normalizeTargetHref = (target: string | URL) => (target instanceof URL ? target.href : /^[a-z]+:\/\//i.test(target) ? new URL(target).href : toFileUri(target));

function ensureBrowserProcessEnv() {
  // esm.sh's browser build still expects process/global shims in a few paths; without them the client import can fail only after hydration/navigation, which is painful to debug.
  const currentProcess = (globalThis as { process?: { env?: Record<string, string | undefined>; cwd?: () => string } }).process;
  const sharedProcess = currentProcess && typeof currentProcess === 'object' ? currentProcess : { env: {} as Record<string, string | undefined>, cwd: () => '/' };
  sharedProcess.env ??= {};
  sharedProcess.cwd ??= () => '/';
  for (const target of [globalThis, typeof window !== 'undefined' ? window : undefined, typeof self !== 'undefined' ? self : undefined]) {
    if (!target || typeof target !== 'object') continue;
    try {
      Object.defineProperty(target, 'process', { value: sharedProcess, configurable: true, writable: true });
    } catch {
      Reflect.set(target, 'process', sharedProcess);
    }
    try {
      Object.defineProperty(target, 'global', { value: globalThis, configurable: true, writable: true });
    } catch {
      Reflect.set(target, 'global', globalThis);
    }
  }
  try {
    window.eval(
      "var process = globalThis.process || window.process || { env: {}, cwd: function(){ return '/'; } }; process.env = process.env || {}; process.cwd = process.cwd || function(){ return '/'; }; window.process = process; globalThis.process = process; globalThis.global = globalThis;",
    );
  } catch {
    // Ignore shim-injection failures here; the later module import path is the real source of truth and will surface actionable errors if this environment patch was actually required.
  }
}

function createA2UIMonacoRuntime(): A2UIMonacoRuntime {
  let modernMonacoPromise: Promise<typeof import('modern-monaco')> | null = null;
  let workspacePromise: Promise<WorkspaceWithInternals> | null = null;
  const modelViewStateCache = new Map<string, ReturnType<MonacoEditor['saveViewState']>>();

  const loadModernMonaco = async () => {
    ensureBrowserProcessEnv();
    modernMonacoPromise ??= window.eval(`import(${JSON.stringify(MONACO_MODULE_URL)})`) as Promise<typeof import('modern-monaco')>; // Cache the remote module import so every page shares one loader/race; repeated dynamic imports were part of the earlier multi-instance instability.
    return modernMonacoPromise;
  };

  const ensureWorkspace = async (initialFilename: string, initialValue: string) => {
    workspacePromise ??= loadModernMonaco().then(({ Workspace }) => {
      const workspace = new Workspace({ name: 'macaron-a2ui-preview', entryFile: initialFilename, initialFiles: { [initialFilename]: initialValue } }) as WorkspaceWithInternals; // The workspace must be shared across preview/gallery routes so every example keeps a stable model identity; separate workspaces were a direct cause of CSR losing sync.
      if (!workspace.__a2uiPatched && workspace._openTextDocument) {
        const originalOpenTextDocument = workspace._openTextDocument.bind(workspace); // We wrap the internal open path because modern-monaco background-opens files for language services, and those opens used to steal the visible editor away from the active debug document.
        workspace._openTextDocument = async (monaco, editor, uri, selectionOrPosition, readonlyContent) => {
          const targetHref = normalizeTargetHref(uri);
          const getDetachedModel = async () => {
            const modelUri = monaco.Uri.parse(targetHref);
            const existing = monaco.editor.getModel(modelUri) as MonacoEditModel | null;
            if (existing) return existing;
            const content = readonlyContent ?? (await workspace.fs.readTextFile(targetHref));
            return monaco.editor.createModel(content, undefined, modelUri) as MonacoEditModel; // Detached models let background file opens succeed without rebinding the visible editor; if this returns through the normal open path, Monaco can jump to a dependency file during navigation.
          };
          const currentHref = (() => {
            try {
              return editor.getModel()?.uri.toString();
            } catch {
              return undefined;
            }
          })();
          const activeHref = workspace.history.state.current;
          if (activeHref !== targetHref || (!selectionOrPosition && currentHref?.startsWith('file://') && currentHref !== targetHref)) return await getDetachedModel(); // Anything that is not the current visible file must stay detached; relaxing this condition is how disposed-editor crashes and model hijacks reappeared before.
          try {
            return await originalOpenTextDocument(monaco, editor, uri, selectionOrPosition, readonlyContent); // Only the true active file is allowed to go through Monaco's visible open flow.
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            if (message.includes('InstantiationService has been disposed') || message.includes('disposed')) return await getDetachedModel(); // When a stale editor dies during navigation, degrade to a detached model instead of surfacing an overlay; otherwise harmless background opens crash the whole debug page.
            throw error;
          }
        };
        workspace.__a2uiPatched = true; // Patch exactly once; stacking wrappers around _openTextDocument compounds the routing logic and makes future regressions almost impossible to reason about.
      }
      return workspace;
    });
    const workspace = await workspacePromise;
    try {
      await workspace.fs.readTextFile(initialFilename);
    } catch {
      await workspace.fs.writeFile(initialFilename, initialValue, { isModelContentChange: false }); // Seed missing files as non-user edits so the workspace starts consistent without triggering watch-driven feedback paths.
    }
    return workspace;
  };
  return { loadModernMonaco, ensureWorkspace, modelViewStateCache };
}

export default function A2UIMonacoRuntimeProvider({ children }: { children: ReactNode }) {
  singletonRuntime ??= createA2UIMonacoRuntime();
  return <A2UIMonacoRuntimeContext.Provider value={singletonRuntime}>{children}</A2UIMonacoRuntimeContext.Provider>;
}

export function useA2UIMonacoRuntime() {
  const runtime = use(A2UIMonacoRuntimeContext);
  if (!runtime) throw new Error('useA2UIMonacoRuntime must be used within A2UIMonacoRuntimeProvider');
  return runtime;
}

export { PREVIEW_FILE, type MonacoEditModel, type MonacoEditor, type MonacoNamespace, type WorkspaceWithInternals };
